import { match } from 'ts-pattern'
import {
  EMPTY_FILTER_SELECTION,
  type FilterableClassification,
  type FilterableFixStatus,
  type FilterablePatchStatus,
  type FilterSelection,
} from '../tables/filter-state-store.tsx'
import {
  areSameValues,
  RESOLUTION_FILTER_CANDIDATES,
  type ResolutionFilter,
  type ResolutionState,
} from './resolution-taxonomy.ts'

/** Triage classifications the design groups as Validated — findings that warrant remediation. */
const VALIDATED_CLASSIFICATIONS: FilterableClassification[] = ['true_positive', 'suspicious']

/**
 * Everything the design shows as Not triaged, including the reasons analysis could not classify.
 *
 * Deliberately not `composition-utils.ts`'s `NOT_TRIAGED_FILTER_KEYS`, which omits `inconclusive`:
 * the composition bar treats Inconclusive as a definitive triage outcome, while the Resolution
 * design groups it under Not triaged. Same words, different groupings — merging them would move
 * findings between segments in the composition bar.
 */
const UNTRIAGED_CLASSIFICATIONS: FilterableClassification[] = [
  'not_triaged',
  'inconclusive',
  'blocked',
  'excluded',
  'error',
]

const DISMISSED_CLASSIFICATIONS: FilterableClassification[] = ['false_positive', 'wont_fix']

/** Fix outcomes that mean Pixee could not produce a fix, so a person has to remediate by hand. */
const NO_FIX_AVAILABLE_RESULTS: FilterableFixStatus[] = [
  'failed',
  'no-recommendations-available',
  'blocked',
  'excluded',
]

/**
 * Maps one resolution state onto the findings table's filters.
 *
 * Every state constrains each dimension it can, even where a narrower constraint would do on its
 * own — `fixed` names its classification and fix result as well as its merged patch status. That
 * redundancy is what makes the union in `toCombinedFilterSelection` come out exact; drop it and
 * aggregates such as Triaged widen to the whole scan.
 *
 * Known imprecision in `fixReady`: the API's `include-patch-status` accepts `issued` (has a patch,
 * merged or not) and `merged`, but nothing meaning "has a patch that is not merged". Fix ready
 * therefore selects validated findings with a completed fix, which also matches findings whose fix
 * is already merged — so it shows a superset that includes Fixed findings.
 *
 * The `unmerged` value that fixes this is implemented in pixee-platform#6513
 * <https://github.com/pixee/pixee-platform/pull/6513>. Once that ships and the OpenAPI spec is
 * synced, `fixReady` takes `patchStatuses: ['unmerged']` and this approximation goes away —
 * tracked in ISS-8781 <https://app.devrev.ai/pixee/works/ISS-8781>.
 */
export const toFilterSelection = (state: ResolutionState): FilterSelection =>
  match(state)
    .with('fixed', () => ({
      classifications: VALIDATED_CLASSIFICATIONS,
      fixResults: ['completed'] satisfies FilterableFixStatus[],
      patchStatuses: ['merged'] satisfies FilterablePatchStatus[],
    }))
    .with('fixReady', () => ({
      classifications: VALIDATED_CLASSIFICATIONS,
      fixResults: ['completed'] satisfies FilterableFixStatus[],
      patchStatuses: [],
    }))
    .with('acknowledged', () => ({
      classifications: VALIDATED_CLASSIFICATIONS,
      fixResults: NO_FIX_AVAILABLE_RESULTS,
      patchStatuses: [],
    }))
    .with('untriaged', () => ({ classifications: UNTRIAGED_CLASSIFICATIONS, fixResults: [], patchStatuses: [] }))
    .with('dismissed', () => ({ classifications: DISMISSED_CLASSIFICATIONS, fixResults: [], patchStatuses: [] }))
    .exhaustive()

/**
 * ORs one dimension across the states in a filter. A state that leaves the dimension unconstrained
 * matches every value in it, so the union is unconstrained too — taking the union of the non-empty
 * lists instead would exclude findings the aggregate is supposed to include.
 */
const unionDimension = <TValue extends string>(lists: TValue[][]): TValue[] =>
  lists.some(list => list.length === 0) ? [] : [...new Set(lists.flat())]

/**
 * Combines the selections for every state in a filter, so an aggregate row such as Triaged filters
 * to the union of its children rather than to a comma list of them.
 */
export const toCombinedFilterSelection = (states: readonly ResolutionState[]): FilterSelection => {
  if (states.length === 0) {
    return EMPTY_FILTER_SELECTION
  }
  const selections = states.map(toFilterSelection)

  return {
    classifications: unionDimension(selections.map(selection => selection.classifications)),
    fixResults: unionDimension(selections.map(selection => selection.fixResults)),
    patchStatuses: unionDimension(selections.map(selection => selection.patchStatuses)),
  }
}

export const areSelectionsEqual = (a: FilterSelection, b: FilterSelection): boolean =>
  areSameValues(a.classifications, b.classifications) &&
  areSameValues(a.fixResults, b.fixResults) &&
  areSameValues(a.patchStatuses, b.patchStatuses)

/**
 * Recognises which panel filter the findings table is currently filtered by, if any.
 *
 * The panel reads this from the filter store rather than remembering its own selection, so it
 * still highlights the right segment when the filter did not come from a click in this session —
 * a filter restored from storage on page load, for instance.
 */
export const toActiveResolutionFilter = (selection: FilterSelection): ResolutionFilter | undefined =>
  RESOLUTION_FILTER_CANDIDATES.find(candidate =>
    areSelectionsEqual(toCombinedFilterSelection(candidate.states), selection)
  )
